import { Component, createRef, createContext } from 'preact';
import { resolveLocalImageUrl } from '../utils/localApi';
import { createPortal } from 'preact/compat';
import { useRef, useState, useCallback, useEffect } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import { playUI, UI } from '../utils/arena/uiSounds';
import { extractKeywordAbilities, getGlossaryEntry } from '../utils/game/sorceryKeywords';
import { rulesCatalog, rulesLabel, localizeTypeLine } from '../localization/rules';
import { useRulesLanguage } from '../localization/useRulesLanguage';
import { t } from '../localization/t';
import { STATUS_EFFECTS } from '../utils/game/cardMesh';
import { isFoilFinish, FOIL_LABEL, FOIL_LABEL_COLOR } from '../utils/sorcery/foil.js';
import { printingDisplayNameFromImageUrl } from '../utils/sorcery/altArtNames';
import { foilZonesForCard } from '../utils/sorcery/foilZones.js';
import FoilPreview from './FoilPreview';
import { DEFAULT_FOIL_PARAMS } from '../utils/sorcery/foilParams.js';
import { Sparkles, ExternalLink, ChevronLeft, Unlink } from 'lucide-react';
import { CloseButton } from './ui/navButtons';
import { Tooltip } from './ui/tooltip';
import { IconMask } from './ui/icons';
import CardImagePlaceholder from './CardImagePlaceholder';
import CardFlipButton from './CardFlipButton';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import {
  GOLD, GOLD_TEXT, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  VIGNETTE, RADIUS,
  TEXTURED_PANEL_STYLE, SUB_PANEL_STYLE, FourCorners, CornerPlating, OrnamentalDivider,
} from '../lib/medievalTheme';

// Rarity-keyed rim glow applied to the info panel. Layered halos grow
// in spread + intensity with rarity so the panel telegraphs the card's
// tier at a glance, before any text is read.
const RARITY_GLOW = {
  Ordinary: '0 0 8px rgba(255,255,255,0.05)',
  Exceptional: '0 0 18px rgba(59,130,246,0.32), 0 0 40px rgba(59,130,246,0.12)',
  Elite: '0 0 22px rgba(168,85,247,0.42), 0 0 50px rgba(168,85,247,0.16)',
  Unique: '0 0 28px rgba(245,158,11,0.5), 0 0 60px rgba(245,158,11,0.2), 0 0 90px rgba(245,158,11,0.08)',
  Avatar: '0 0 30px rgba(220,40,40,0.45), 0 0 64px rgba(220,40,40,0.18), 0 0 100px rgba(245,158,11,0.08)',
};

// Inline icons for rules text. Card data contains ASCII tokens like
// "(A)" / "(W)" / "(E)" / "(F)" for element thresholds, "(0)"–"(9)" /
// "(X)" for mana amounts, and unicode circled digits ①–⑩ for mana. We
// replace each with a themed glyph so rules text reads cleanly.
const ELEMENT_TRIANGLES = {
  A: { element: 'Air', points: '6,1 11,10 1,10', line: [2.5, 7, 9.5, 7], color: '#A0BADB' },
  W: { element: 'Water', points: '6,11 1,2 11,2', line: null, color: '#01FFFF' },
  E: { element: 'Earth', points: '6,11 1,2 11,2', line: [2.5, 5, 9.5, 5], color: '#CFA572' },
  F: { element: 'Fire', points: '6,1 11,10 1,10', line: null, color: '#FF5F00' },
};

// Sized in `em` so the icon tracks whatever cqw-scaled text it's
// embedded in. Tailwind's text-* tokens are redefined in cqw, so 1em
// inherits both canvas-level scaling and the local text-size context
// (text-sm in the main panel vs text-xs inside popovers).
function SorceryElementIcon({ code }) {
  const t = ELEMENT_TRIANGLES[code];
  if (!t) return null;
  return (
    <svg
      viewBox="0 0 12 12"
      width="1em"
      height="1em"
      className="inline-block mx-0.5"
      style={{ verticalAlign: 'middle' }}
      fill="none"
      stroke={t.color}
      strokeWidth="1.5"
      strokeLinejoin="round"
      aria-label={t.element}
    >
      <polygon points={t.points} />
      {t.line ? <line x1={t.line[0]} y1={t.line[1]} x2={t.line[2]} y2={t.line[3]} /> : null}
    </svg>
  );
}

// Nested spans: outer keeps the parent font-size so its 1em
// width/height resolve to the surrounding text height; inner sets its
// own font-size for the digit. If both lived on one element, em on
// width would compute against the smaller font-size and shrink the
// circle out of proportion.
function ManaIcon({ value }) {
  return (
    <span
      className="inline-flex items-center justify-center mx-0.5 font-bold select-none"
      style={{
        width: '1em',
        height: '1em',
        borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 30%, #f5cd55, #a97c1f 82%)',
        border: '1px solid rgba(0,0,0,0.45)',
        color: '#2a1a05',
        lineHeight: 1,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 1px 1px rgba(0,0,0,0.4)',
        verticalAlign: 'middle',
      }}
      aria-label={t('{n} mana', { n: value })}
    >
      <span style={{ fontSize: '0.72em', lineHeight: 1 }}>{value}</span>
    </span>
  );
}

const INLINE_TOKEN_RE = /(\([AEFW]\)|\([\dX]\)|[\u2460-\u2469])/g;
const CIRCLED_DIGIT_VALUE = {
  '\u2460': 1, '\u2461': 2, '\u2462': 3, '\u2463': 4, '\u2464': 5,
  '\u2465': 6, '\u2466': 7, '\u2467': 8, '\u2468': 9, '\u2469': 10,
};

function renderInlineTokens(text) {
  if (!text) return text;
  const parts = text.split(INLINE_TOKEN_RE);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (i % 2 === 0) return part;
    const elt = /^\(([AEFW])\)$/.exec(part);
    if (elt) return <SorceryElementIcon key={i} code={elt[1]} />;
    const mana = /^\(([\dX])\)$/.exec(part);
    if (mana) return <ManaIcon key={i} value={mana[1]} />;
    const circled = CIRCLED_DIGIT_VALUE[part];
    if (circled != null) return <ManaIcon key={i} value={String(circled)} />;
    return part;
  });
}

const POPOVER_OFFSET = 8;
const POPOVER_VIEWPORT_PAD = 8;
const TOOLTIP_SHOW_EVENT = 'vh-glossary-tooltip-show';
const TOOLTIP_BRIDGE_ENTER = 'vh-glossary-tooltip-bridge-enter';
const TOOLTIP_BRIDGE_LEAVE = 'vh-glossary-tooltip-bridge-leave';
const TOOLTIP_MODAL_OPEN = 'vh-glossary-tooltip-modal-open';
const TOOLTIP_MODAL_CLOSE = 'vh-glossary-tooltip-modal-close';

// Glossary tooltips and the "read more" modal both portal to
// `.app-canvas`, so they're DOM siblings and stack purely by z-index.
// Tooltips use a depth-stepped base; the modal sits above the base
// tooltips. Tooltips spawned *inside* the modal must clear it, so
// KeywordModal raises this base for its subtree via TooltipZContext.
// Context (not depth) carries the layer, because `depth` can't tell a
// nested hover popover (stay below the modal) from a tooltip born in
// the modal (rise above it).
const TOOLTIP_Z_BASE = 1300;
const KEYWORD_MODAL_Z = 1400;
const MODAL_TOOLTIP_Z_BASE = KEYWORD_MODAL_Z + 10;
const TooltipZContext = createContext(TOOLTIP_Z_BASE);

class GlossaryTerm extends Component {
  static contextType = TooltipZContext;

  constructor(props) {
    super(props);
    this.state = { open: false, pos: null, modalOpen: false, skipExit: false };
    this.timer = null;
    this._instanceId = (GlossaryTerm._idCounter = (GlossaryTerm._idCounter || 0) + 1);
    this.triggerRef = createRef();
    this.popoverRef = createRef();
  }

  componentDidMount() {
    window.addEventListener(TOOLTIP_SHOW_EVENT, this._handleOtherShow);
    window.addEventListener(TOOLTIP_BRIDGE_ENTER, this._handleBridgeEnter);
    window.addEventListener(TOOLTIP_BRIDGE_LEAVE, this._handleBridgeLeave);
    window.addEventListener(TOOLTIP_MODAL_OPEN, this._handleModalOpen);
    window.addEventListener(TOOLTIP_MODAL_CLOSE, this._handleModalClose);
  }

  componentDidUpdate(_, prevState) {
    if (this.state.open && !prevState.open) this.position();
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
    window.removeEventListener(TOOLTIP_SHOW_EVENT, this._handleOtherShow);
    window.removeEventListener(TOOLTIP_BRIDGE_ENTER, this._handleBridgeEnter);
    window.removeEventListener(TOOLTIP_BRIDGE_LEAVE, this._handleBridgeLeave);
    window.removeEventListener(TOOLTIP_MODAL_OPEN, this._handleModalOpen);
    window.removeEventListener(TOOLTIP_MODAL_CLOSE, this._handleModalClose);
  }

  _handleModalOpen = (e) => {
    // Any descendant tooltip's "read more" modal opening locks us
    // open until the modal closes. Otherwise the modal covering the
    // child popover would fire a mouseLeave that cascades up and
    // unmounts the whole tree, taking the modal with it.
    const myDepth = this.props.depth || 0;
    const modalDepth = e.detail?.depth ?? 0;
    if (modalDepth >= myDepth) {
      this._modalLocked = true;
      clearTimeout(this.timer);
      this.timer = null;
    }
  };

  _handleModalClose = (e) => {
    const myDepth = this.props.depth || 0;
    const modalDepth = e.detail?.depth ?? 0;
    if (modalDepth >= myDepth) {
      this._modalLocked = false;
      // Hand off to normal close behaviour: if our cursor isn't on
      // us when the modal closes, start dismissing.
      if (this.state.open && !this.state.mouseOnTooltip) this.hide();
    }
  };

  _handleOtherShow = (e) => {
    // Sibling/parent-level tooltip taking over: force-close instantly.
    // A deeper-depth show event means it opened inside our own popover
    // (nested tooltip), so we stay visible.
    if (e.detail?.id === this._instanceId) return;
    const myDepth = this.props.depth || 0;
    const newDepth = e.detail?.depth ?? 0;
    if (newDepth > myDepth) return;
    if (this.state.open) {
      clearTimeout(this.timer);
      this.timer = null;
      this.setState({ open: false, pos: null, skipExit: true });
    }
  };

  _handleBridgeEnter = (e) => {
    // A descendant popover was entered. Keep ourselves open by
    // cancelling any pending hide timer. The cursor is "still on the
    // tooltip stack," even if it's now over a deeper child.
    if (e.detail?.id === this._instanceId) return;
    const myDepth = this.props.depth || 0;
    const enteredDepth = e.detail?.depth ?? 0;
    if (enteredDepth >= myDepth && this.state.open) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  };

  _handleBridgeLeave = (e) => {
    // A popover at our depth or deeper was left. If our own pointer is
    // not on us, we should also start dismissing. The user has left
    // the tooltip stack entirely. Skip while a read-more modal has us
    // locked open.
    if (this._modalLocked) return;
    if (e.detail?.id === this._instanceId) return;
    const myDepth = this.props.depth || 0;
    const leftDepth = e.detail?.depth ?? 0;
    if (leftDepth >= myDepth && this.state.open && !this.state.mouseOnTooltip && !this.timer) {
      this.hide();
    }
  };

  show = () => {
    clearTimeout(this.timer);
    this.timer = null;
    window.dispatchEvent(new CustomEvent(TOOLTIP_SHOW_EVENT, {
      detail: { id: this._instanceId, depth: this.props.depth || 0 },
    }));
    this.setState({ open: true, pos: null, skipExit: false, mouseOnTooltip: true });
  };

  hide = () => {
    if (this.timer) return;
    if (this._modalLocked) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.state.mouseOnTooltip || this._modalLocked) return;
      this.setState({ open: false, pos: null, skipExit: false });
    }, 60);
  };

  _handlePopoverEnter = () => {
    clearTimeout(this.timer);
    this.timer = null;
    this.setState({ mouseOnTooltip: true });
    window.dispatchEvent(new CustomEvent(TOOLTIP_BRIDGE_ENTER, {
      detail: { id: this._instanceId, depth: this.props.depth || 0 },
    }));
  };

  _handlePopoverLeave = () => {
    this.setState({ mouseOnTooltip: false });
    window.dispatchEvent(new CustomEvent(TOOLTIP_BRIDGE_LEAVE, {
      detail: { id: this._instanceId, depth: this.props.depth || 0 },
    }));
    this.hide();
  };

  _handleTriggerEnter = () => {
    this.setState({ mouseOnTooltip: true });
    this.show();
  };

  _handleTriggerLeave = () => {
    this.setState({ mouseOnTooltip: false });
    window.dispatchEvent(new CustomEvent(TOOLTIP_BRIDGE_LEAVE, {
      detail: { id: this._instanceId, depth: this.props.depth || 0 },
    }));
    this.hide();
  };

  /**
   * Measure trigger + popover and pick a position that keeps the entire
   * popover inside the canvas. Prefer above the trigger; flip below if
   * not enough room. Clamp to canvas bounds, not viewport. The popover
   * is portaled inside `.app-canvas`, which is a transform-containing
   * block, so position: fixed is canvas-relative.
   */
  position = () => {
    const trigger = this.triggerRef.current;
    const pop = this.popoverRef.current;
    if (!trigger || !pop) return;

    const canvas = document.querySelector('.app-canvas');
    const cRect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    const tRect = trigger.getBoundingClientRect();
    const pRect = pop.getBoundingClientRect();
    const padX = POPOVER_VIEWPORT_PAD;
    const padY = POPOVER_VIEWPORT_PAD;

    // Convert trigger to canvas-local coords.
    const tLocalLeft = tRect.left - cRect.left;
    const tLocalTop = tRect.top - cRect.top;
    const tLocalBottom = tRect.bottom - cRect.top;

    // Vertical: prefer above the trigger; flip below if no room.
    let top = tLocalTop - pRect.height - POPOVER_OFFSET;
    if (top < padY) top = tLocalBottom + POPOVER_OFFSET;
    if (top + pRect.height > cRect.height - padY) {
      top = Math.max(padY, cRect.height - pRect.height - padY);
    }

    // Horizontal: center on trigger, clamp to canvas.
    let left = tLocalLeft + tRect.width / 2 - pRect.width / 2;
    left = Math.max(padX, Math.min(left, cRect.width - pRect.width - padX));

    this.setState({ pos: { left, top } });
  };

  openModal = () => {
    clearTimeout(this.timer);
    this.timer = null;
    const myDepth = this.props.depth || 0;
    // Lock ourselves and any ancestor tooltips open while the modal
    // is up so the modal's portal subtree doesn't get torn down by a
    // mouseLeave cascade when the modal covers the popover.
    this._modalLocked = true;
    this.setState({ open: false, pos: null, modalOpen: true, mouseOnTooltip: false });
    window.dispatchEvent(new CustomEvent(TOOLTIP_MODAL_OPEN, {
      detail: { id: this._instanceId, depth: myDepth },
    }));
  };

  closeModal = () => {
    const myDepth = this.props.depth || 0;
    this._modalLocked = false;
    this.setState({ modalOpen: false });
    window.dispatchEvent(new CustomEvent(TOOLTIP_MODAL_CLOSE, {
      detail: { id: this._instanceId, depth: myDepth },
    }));
  };

  render() {
    const { entry, renderNested, depth, children, language = 'en' } = this.props;
    const { open, pos, modalOpen } = this.state;

    const popover = (
      <AnimatePresence>
        {open ? createPortal(
          <motion.span
            key="glossary-popover"
            ref={this.popoverRef}
            data-tooltip-popover=""
            className="fixed inline-block"
            style={{
              left: pos?.left ?? -9999,
              top: pos?.top ?? -9999,
              visibility: pos ? 'visible' : 'hidden',
              zIndex: this.context + depth * 10,
            }}
            initial={{ opacity: 0, y: 3 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: 3,
              transition: { duration: this.state.skipExit ? 0 : 0.08, ease: 'easeOut' },
            }}
            transition={{ duration: 0.1, ease: [0.2, 0.7, 0.3, 1] }}
            onMouseEnter={this._handlePopoverEnter}
            onMouseLeave={this._handlePopoverLeave}
          >
            <span
              className="block w-72 p-3 text-xs"
              style={TEXTURED_PANEL_STYLE}
            >
              <span className="font-semibold block mb-1" style={{ color: TEXT_PRIMARY }}>{entry.keyword}</span>
              <ClampedDescription
                language={language}
                keyword={entry.keyword}
                description={entry.description}
                renderText={renderNested}
                lines={6}
                textClassName="text-xs"
                onOpenModal={this.openModal}
              />
            </span>
          </motion.span>,
          document.querySelector('.app-canvas') ?? document.body,
        ) : null}
      </AnimatePresence>
    );

    // Modal is rendered as a sibling of the popover at the trigger's
    // level so its lifetime is decoupled from hover state. Without
    // this, mousing onto the modal closes the popover and unmounts
    // the modal along with it.
    const modal = modalOpen ? (
      <KeywordModal
        language={language}
        keyword={entry.keyword}
        description={entry.description}
        renderText={renderNested}
        onClose={this.closeModal}
      />
    ) : null;

    return (
      <span
        data-rules-term={entry.id || entry.keyword}
        ref={this.triggerRef}
        className="relative inline"
        onMouseEnter={this._handleTriggerEnter}
        onMouseLeave={this._handleTriggerLeave}
      >
        {children ?? (
          <span className="cursor-help" style={{ color: ACCENT_GOLD, textShadow: `0 0 8px ${GOLD} 0.2)` }}>{entry.keyword}</span>
        )}
        {popover}
        {modal}
      </span>
    );
  }
}

export function renderTextWithTooltips(text, depth = 0, language = 'en') {
  if (!text || depth > 2) return text;
  return <span lang={language} className={depth === 0 ? 'whitespace-pre-line' : ''}>
    {rulesCatalog.segments(text, language).map((part, i) => part.entry ? (
      <GlossaryTerm key={i} language={part.entry.language}
        entry={{ ...part.entry, id: part.entry.keyword, keyword: part.entry.label }}
        renderNested={(value) => renderTextWithTooltips(value, depth + 1, part.entry.language)} depth={depth}>
        <span className="cursor-help" style={{ color: ACCENT_GOLD }}>{part.text}</span>
      </GlossaryTerm>
    ) : <span key={i}>{renderInlineTokens(part.text)}</span>)}
  </span>;
}

export const RARITY_COLORS = {
  Ordinary: TEXT_MUTED,
  Exceptional: '#6ea8d4',
  Elite: '#b480d4',
  Unique: ACCENT_GOLD,
  Avatar: '#c45050',
};

export const RARITY_LABEL_COLOR = {
  Ordinary: 'text-white/40',
  Exceptional: 'text-blue-400',
  Elite: 'text-purple-400',
  Unique: 'text-amber-400',
  Avatar: 'text-red-400',
};

const STAT_COLORS = {
  ATK: '#c45050',
  DEF: '#6ea8d4',
  HP: '#6ab04c',
  Cost: ACCENT_GOLD,
};

const TILT_MAX = 12;

// curiosa.io card URLs use underscore-separated slugs of the card
// name (e.g. /cards/belmotte_longbowmen, /cards/avatar_of_fire).
// Apostrophes, exclamation marks, and parentheses are dropped, NOT
// turned into underscores. "Angel's Egg" → angels_egg (not
// angel_s_egg, which 500s on curiosa). Accents are stripped via NFD
// normalization so "Brocéliande" → broceliande. Everything else
// non-alphanumeric (hyphens, commas, spaces) collapses to a single
// underscore.
function curiosaCardUrl(name) {
  if (!name) return null;
  const slug = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['‘’!()]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return slug ? `https://curiosa.io/cards/${slug}` : null;
}

const STATUS_BY_KEY = new Map(STATUS_EFFECTS.map((e) => [e.key, e]));

function ActiveStatusRow({ statuses, language = 'en' }) {
  if (!statuses || statuses.length === 0) return null;
  // wrap-reverse so overflow stacks UPWARD: the first row sits flush
  // against the description box below, and additional chips appear on
  // new rows above it.
  return (
    <div className="flex items-center gap-2" style={{ flexWrap: 'wrap-reverse' }}>
      {statuses.map((key) => {
        const effect = STATUS_BY_KEY.get(key);
        if (!effect) return null;
        const localizedEntry = rulesCatalog.entry(effect.label, language);
        const entry = localizedEntry ? { ...localizedEntry, id: localizedEntry.keyword, keyword: localizedEntry.label } : getGlossaryEntry(effect.label);
        const labelNode = (
          <span
            className="text-[0.6875cqw] font-semibold inline-flex items-center"
            style={{
              color: entry ? ACCENT_GOLD : TEXT_PRIMARY,
              textShadow: entry ? `0 0 8px ${GOLD} 0.2)` : '0 1px 2px rgba(0,0,0,0.6)',
              cursor: entry ? 'help' : 'default',
              lineHeight: 1,
              marginRight: '-0.05em',
              // Lift the cap block up to compensate for the empty
              // descender area below the baseline. Without this,
              // items-center centers the line box, but the visible
              // caps sit in the upper half of that box, leaving more
              // whitespace above than below.
              transform: 'translateY(-1px)',
            }}
          >
            {rulesCatalog.term(effect.label, language)}
          </span>
        );
        return (
          <div
            key={key}
            className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full leading-none"
            style={{
              background: 'rgba(20, 16, 12, 0.7)',
              border: `1px solid ${GOLD} 0.35)`,
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.05), 0 2px 6px rgba(0,0,0,0.4), 0 0 10px ${effect.color}22`,
            }}
          >
            <span
              className="inline-flex items-center justify-center font-bold"
              style={{
                width: '1.375cqw',
                height: '1.375cqw',
                borderRadius: '50%',
                border: '1.5px solid rgba(220,180,80,0.85)',
                background: `radial-gradient(circle at 40% 35%, ${effect.color}ee, ${effect.color}bb 55%, ${effect.color}88 100%)`,
                color: '#fff',
                fontSize: '0.5cqw',
                letterSpacing: '0.03em',
                textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                boxShadow: `0 0 10px ${effect.color}55, inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.3)`,
                lineHeight: 1,
              }}
            >
              {effect.abbr}
            </span>
            {entry ? (
              <GlossaryTerm
                language={language}
                entry={entry}
                renderNested={(t) => renderTextWithTooltips(t, 1, entry.language || 'en')}
                depth={0}
              >
                {labelNode}
              </GlossaryTerm>
            ) : labelNode}
          </div>
        );
      })}
    </div>
  );
}

// Descriptions longer than this character count get clamped with a
// subtle "read more" link that opens a scrollable overlay modal. Short
// glossary blurbs render in full so quick references stay frictionless.
const CLAMP_THRESHOLD = 220;

function KeywordModal({ keyword, description, renderText, onClose, language = 'en' }) {
  useEffect(() => {
    const id = pushDismiss(() => onClose());
    return () => removeDismiss(id);
  }, [onClose]);

  return createPortal(
    <TooltipZContext.Provider value={MODAL_TOOLTIP_Z_BASE}>
      <div
        role="dialog"
        lang={language}
        aria-modal="true"
        className="fixed inset-0 flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(10px)', zIndex: KEYWORD_MODAL_Z }}
        onClick={onClose}
      >
        <div
          className="relative flex flex-col p-5 max-w-[min(1100px,max(900px,75cqw))] w-[88cqw] max-h-[80cqh]"
          style={TEXTURED_PANEL_STYLE}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners />
          <div className="absolute top-3 right-3">
            <CloseButton onClick={onClose} />
          </div>
          <h3 className="text-lg font-bold arena-heading pr-10 shrink-0" style={{ color: TEXT_PRIMARY }}>{keyword}</h3>
          <OrnamentalDivider className="my-3 shrink-0" />
          <div
            className="text-sm leading-relaxed overflow-y-auto pr-2 flex-1 min-h-0 whitespace-pre-line"
            style={{ color: TEXT_BODY }}
          >
            {renderText(description, 1)}
          </div>
        </div>
      </div>
    </TooltipZContext.Provider>,
    document.querySelector('.app-canvas') ?? document.body,
  );
}

// Shared clamped-text body. Used both inside keyword ability boxes
// and inside the hover popovers shown for inline glossary terms.
// Provides a uniform "read more" affordance and modal trigger.
function ClampedDescription({ keyword, description, renderText, lines = 6, textClassName = 'text-xs', onOpenModal, language = 'en' }) {
  // When a parent provides `onOpenModal`, modal control is lifted up
  // (e.g. inside hover popovers, where this component would unmount
  // on mouseleave and take the modal with it). Otherwise the modal is
  // owned locally for self-contained use inside KeywordBox.
  const [internalOpen, setInternalOpen] = useState(false);
  const isLong = description.length > CLAMP_THRESHOLD;

  // German explanations are complete text, not automatically cut summaries.
  const clampStyle = isLong && language !== 'en' ? { maxHeight: '14cqh', overflowY: 'auto' } : isLong ? {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  } : null;

  const stop = (e) => e.stopPropagation();
  const handleReadMore = (e) => {
    stop(e);
    if (onOpenModal) onOpenModal();
    else setInternalOpen(true);
  };

  return (
    <>
      <div
        lang={language}
        className={`${textClassName} leading-relaxed`}
        style={{ color: TEXT_BODY, ...clampStyle }}
      >
        {renderText(description, 1)}
      </div>
      {isLong ? (
        <button
          type="button"
          onClick={handleReadMore}
          onMouseDown={stop}
          className="mt-0.5 inline-block text-[0.6875cqw] font-medium cursor-pointer hover:underline"
          style={{ color: ACCENT_GOLD, opacity: 0.85 }}
        >
          {rulesLabel('read more…', language)}
        </button>
      ) : null}
      {internalOpen && !onOpenModal ? (
        <KeywordModal
          language={language}
          keyword={keyword}
          description={description}
          renderText={renderText}
          onClose={() => setInternalOpen(false)}
        />
      ) : null}
    </>
  );
}

function KeywordBox({ keyword, description, renderText, language = 'en' }) {
  return (
    <div className="relative p-3" style={SUB_PANEL_STYLE}>
      <div className="text-sm font-semibold mb-1" style={{ color: TEXT_PRIMARY }}>{keyword}</div>
      <ClampedDescription
        language={language}
        keyword={keyword}
        description={description}
        renderText={renderText}
        lines={6}
      />
    </div>
  );
}

// Dragonlord companion panel. The third inspector column. Shows the
// set-aside Dragon's art, name, and rules text next to (not inside)
// the avatar's own text box so the player reads both ability sets at
// once. Two presentations: ACTIVE (merged this turn. Amber rim glow,
// pulsing status dot, ember-tinted header) and SET ASIDE (muted, with
// the activation reminder).
function GainedAbilitiesPanel({ gained, language = 'en' }) {
  const { card, imageUrl, active } = gained;
  const localized = rulesCatalog.card(card, 'front', language);
  const text = localized.text;
  const renderText = (value) => renderTextWithTooltips(value, 0, localized.language);
  const accent = active ? ACCENT_GOLD : TEXT_MUTED;
  // "If you meet its threshold." Spell the full activation
  // requirement out in the set-aside state so the player knows WHY
  // the invoke button may be disabled (the threshold gate is the
  // non-obvious one; mana alone doesn't unlock it). The (A)/(E)/(F)/
  // (W) and (3) tokens render as the usual inline glyphs.
  const thresholdTokens = [
    ...Array(card.airThreshold || 0).fill('(A)'),
    ...Array(card.earthThreshold || 0).fill('(E)'),
    ...Array(card.fireThreshold || 0).fill('(F)'),
    ...Array(card.waterThreshold || 0).fill('(W)'),
  ].join('');
  const activationHint = thresholdTokens
    ? t('Requires {tokens} threshold. Pay (3) on your turn to gain these abilities.', { tokens: thresholdTokens })
    : t('Pay (3) on your turn to gain these abilities.');
  return (
    <div
      className="relative flex-shrink-0 w-[19cqw] p-4 self-start"
      style={{
        ...TEXTURED_PANEL_STYLE,
        boxShadow: active
          ? `${TEXTURED_PANEL_STYLE.boxShadow}, 0 0 30px rgba(245,158,11,0.45), 0 0 64px rgba(245,158,11,0.16)`
          : TEXTURED_PANEL_STYLE.boxShadow,
      }}
    >
      <FourCorners />

      <div className="flex items-center justify-between gap-2">
        <span
          className="text-[0.7cqw] font-bold"
          style={{ color: accent, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
        >
          {active ? t('Gained Abilities') : t('Chosen Champion')}
        </span>
        {active && (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[0.6cqw] font-semibold"
            style={{
              background: `${GOLD} 0.14)`,
              border: `1px solid ${GOLD} 0.5)`,
              color: 'rgb(240, 205, 120)',
            }}
          >
            <motion.span
              className="inline-block rounded-full"
              style={{ width: '0.4cqw', height: '0.4cqw', background: ACCENT_GOLD }}
              animate={{ opacity: [1, 0.3, 1], scale: [1, 0.8, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            />
            {rulesLabel('Active this turn', language)}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-start gap-3">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={card.name}
            className="flex-shrink-0 rounded-card"
            style={{
              width: '4.5cqw',
              border: `1px solid ${active ? `${GOLD} 0.45)` : `${GOLD} 0.2)`}`,
              boxShadow: active
                ? `0 2px 8px rgba(0,0,0,0.6), 0 0 14px ${GOLD} 0.35)`
                : '0 2px 8px rgba(0,0,0,0.6)',
              filter: active ? 'none' : 'saturate(0.55) brightness(0.85)',
            }}
          />
        ) : null}
        <div className="min-w-0">
          <div className="text-sm font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
            {card.name}
          </div>
          <div className="mt-0.5 text-xs" style={{ color: TEXT_MUTED }}>
            {localizeTypeLine(card.type_text || card.type || '', language)}
          </div>
        </div>
      </div>

      {text ? (
        <>
          <div className="my-3 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD} 0.2), transparent)` }} />
          <div
            className="text-sm leading-relaxed overflow-y-auto pr-1"
            style={{ color: active ? TEXT_BODY : TEXT_MUTED, maxHeight: '22cqh' }}
          >
            {renderText(text)}
          </div>
        </>
      ) : null}

      {!active ? (
        <div lang={language} className="mt-3 text-xs leading-relaxed" style={{ color: TEXT_MUTED }}>
          {renderTextWithTooltips(activationHint, 0, language)}
        </div>
      ) : null}
    </div>
  );
}

function InspectorCard({ imgSrc, cardName, isSite, isFoil, foiling, hasZones = true }) {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, mx: 50, my: 50, active: false });
  // Image load tracking. Mirrors DeckCardTile. The placeholder shows
  // while the fetch is in flight; on a 404/onError `imgFailed` flips so
  // the spinner is hidden (the framed placeholder stays) instead of the
  // browser's broken-image glyph or a spinner that never resolves.
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  // Seed load state from the live DOM element rather than blindly
  // resetting to "loading." The art is almost always already cached by
  // the time the inspector opens (it was shown in the grid/on the board
  // first), so the browser fires `load` SYNCHRONOUSLY when Preact swaps
  // the <img> src during commit. Before this effect runs. A naive
  // `setImgLoaded(false)` here would clobber that already-fired onLoad
  // and strand the placeholder forever (the image is `complete`, so no
  // second `load` event is coming). Reading `complete`/`naturalWidth`
  // after commit reflects the new src and wins the race deterministically.
  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete) {
      // Already settled in cache before this effect ran. naturalWidth > 0
      // means decoded OK; == 0 with complete means the load broke (404).
      // Seed both flags so a synchronously-fired onLoad/onError isn't
      // clobbered back to a perpetual "loading" placeholder.
      setImgLoaded(el.naturalWidth > 0);
      setImgFailed(el.naturalWidth === 0);
    } else {
      setImgLoaded(false);
      setImgFailed(false);
    }
  }, [imgSrc]);

  const handleMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setTilt({
      rx: (0.5 - y) * 2 * TILT_MAX,
      ry: (x - 0.5) * 2 * TILT_MAX,
      mx: x * 100,
      my: y * 100,
      active: true,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ rx: 0, ry: 0, mx: 50, my: 50, active: false });
  }, []);

  const { rx, ry, mx, my, active } = tilt;
  const dx = (mx / 100) - 0.5;
  const dy = (my / 100) - 0.5;
  const fromCenter = Math.min(1, Math.sqrt(dx * dx + dy * dy) / 0.5);

  // Every foil card uses the premium WebGL foil (shared FoilPreview singleton).
  const premiumFoil = isFoil;

  return (
    <div
      ref={ref}
      className="flex-shrink-0"
      style={{ perspective: '800px' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`overflow-hidden card-mask ${isSite ? 'card-mask--landscape rounded-card-landscape' : 'rounded-card'}`}
        style={{
          transform: `rotateX(${rx}deg) rotateY(${ry}deg)`,
          transition: active ? 'transform 0.1s ease-out' : 'transform 0.4s ease-out',
          transformStyle: 'preserve-3d',
          position: 'relative',
          // Always-on colored glow halo so a premium foil reads as foil even
          // at rest (the WebGL canvas is clipped to the card, so the halo is
          // CSS on the wrapper).
          ...(premiumFoil && DEFAULT_FOIL_PARAMS.glowStr > 0
            ? { boxShadow: `0 0 ${14 * DEFAULT_FOIL_PARAMS.glowStr}px rgba(200,170,80,${0.5 * DEFAULT_FOIL_PARAMS.glowStr}), 0 0 ${34 * DEFAULT_FOIL_PARAMS.glowStr}px rgba(200,170,80,${0.18 * DEFAULT_FOIL_PARAMS.glowStr})` }
            : {}),
          ...(isSite
            ? { width: '40vh', height: 'calc(40vh * 63.5 / 88.9)' }
            : { height: '40vh', width: 'calc(40vh * 63.5 / 88.9)' }),
        }}
      >
        {!imgLoaded ? <CardImagePlaceholder spinnerSize={imgFailed ? 0 : 36} /> : null}
        <img
          ref={imgRef}
          src={imgSrc}
          alt={cardName || ''}
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgFailed(true)}
          className="card-image rounded-card"
          style={isSite
            ? { height: '40vh', width: 'calc(40vh * 63.5 / 88.9)', transform: 'rotate(90deg) translateX(0%) translateY(-100%)', transformOrigin: 'top left', opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.3s ease-out' }
            : { height: '40vh', opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.3s ease-out' }
          }
        />
        {premiumFoil && imgSrc ? (
          <FoilPreview imageUrl={imgSrc} tilt={{ mx, my, fromCenter }} zones={hasZones} params={DEFAULT_FOIL_PARAMS} rotate90={isSite} />
        ) : null}
      </div>
    </div>
  );
}

// Sized so the equipment panel always reserves exactly enough room
// for three entries while leaving visible margin between the panel
// and the top / bottom of the canvas. Math derives from a portrait
// card aspect (Sorcery cards print at 63.5×88.9mm). Uniform slot
// height for every entry; total panel height lands near 88cqh, which
// matches the inspector's own max-h-[90cqh] so the two read as
// visually balanced columns instead of one column running edge-to-
// edge while the other floats in the middle of the canvas.
const EQUIPMENT_IMAGE_WIDTH = '7.5cqw';
const EQUIPMENT_IMAGE_HEIGHT = `calc(${EQUIPMENT_IMAGE_WIDTH} * 88.9 / 63.5)`;
// Image + name line + rarity line + detach button + the three
// gap-0.25cqw stacks between them. Empirically ~3.2cqw of text/button
// stack at the typography sizes used below.
const EQUIPMENT_ENTRY_HEIGHT = `calc(${EQUIPMENT_IMAGE_HEIGHT} + 3.2cqw)`;
// 1cqw (~16px @ scale 1) so the Detach button of one entry doesn't
// crowd the next entry's image. Anything smaller and the rows read
// as a single mass; this is the minimum that visually separates the
// cards while still keeping all three visible inside the panel.
const EQUIPMENT_ENTRY_GAP = '1cqw';
const EQUIPMENT_VISIBLE_COUNT = 3;
// The scroll viewport has explicit padding on all four sides so the
// hover-lift drop-shadow on an entry doesn't get clipped at any of
// the four scroll boundaries. Vertical padding is folded into the
// viewport height so three full entries remain visible without
// scrolling; horizontal padding adds breathing room for the glow
// without changing the entries' own widths.
const EQUIPMENT_SCROLL_PADDING_Y = '1cqw';
const EQUIPMENT_SCROLL_PADDING_X = '0.75cqw';
const EQUIPMENT_SCROLL_HEIGHT = `calc(${EQUIPMENT_VISIBLE_COUNT} * ${EQUIPMENT_ENTRY_HEIGHT} + ${EQUIPMENT_VISIBLE_COUNT - 1} * ${EQUIPMENT_ENTRY_GAP} + 2 * ${EQUIPMENT_SCROLL_PADDING_Y})`;

function EquipmentEntry({ eq, full, onInspect, onDetach, locked, lockReason }) {
  // Wire-shaped entry: resolve the art like every other card render (a
  // desktop peer's local path is unloadable on web) and fall back to the
  // catalog when the entry carries none.
  const eqArt = resolveLocalImageUrl(eq.imageUrl || full?.printings?.[0]?.image_url) || null;
  const rarityTone = RARITY_COLORS[full?.rarity] || TEXT_MUTED;
  const [hovered, setHovered] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [eq.imageUrl]);

  return (
    <div
      className="relative flex flex-col items-center gap-1"
      style={{ height: EQUIPMENT_ENTRY_HEIGHT, flex: '0 0 auto' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={() => onInspect?.(eq)}
        className="block cursor-pointer transition-transform"
        style={{
          width: EQUIPMENT_IMAGE_WIDTH,
          height: EQUIPMENT_IMAGE_HEIGHT,
          background: 'transparent',
          border: 'none',
          padding: 0,
          position: 'relative',
          transform: hovered ? 'translateY(-2px) scale(1.02)' : 'translateY(0) scale(1)',
          transition: 'transform 140ms ease',
          filter: hovered ? `drop-shadow(0 8px 18px ${rarityTone}55) drop-shadow(0 0 12px rgba(212,168,67,0.35))` : 'drop-shadow(0 6px 14px rgba(0,0,0,0.6))',
        }}
        title={t('Inspect {name}', { name: eq.name || t('card') })}
      >
        {eqArt && !imgFailed ? (
          <img
            src={eqArt}
            alt={eq.name || ''}
            onError={() => setImgFailed(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              borderRadius: RADIUS.card,
            }}
          />
        ) : (
          <CardImagePlaceholder spinnerSize={0} style={{ borderRadius: RADIUS.card }} />
        )}
      </button>
      <div className="text-center w-full leading-tight">
        <div
          className="text-[0.75cqw] font-semibold truncate px-1"
          style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
          title={eq.name}
        >
          {eq.name || t('Unknown')}
        </div>
        <div className="text-[0.625cqw]" style={{ color: rarityTone }}>
          {full?.rarity || (eq.type || t('Card'))}
        </div>
      </div>
      <button
        type="button"
        onClick={() => { if (!locked) onDetach?.(eq.id); }}
        disabled={!!locked}
        title={locked ? (lockReason || t('Locked in place by an aura')) : t('Detach {name}', { name: eq.name || t('card') })}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[0.625cqw] font-semibold transition-colors"
        style={{
          background: 'rgba(20,16,12,0.7)',
          border: `1px solid ${GOLD} 0.35)`,
          borderRadius: 'var(--radius-md)',
          color: locked ? TEXT_MUTED : TEXT_BODY,
          textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          cursor: locked ? 'not-allowed' : 'pointer',
          opacity: locked ? 0.55 : 1,
        }}
        onMouseEnter={(e) => { if (locked) return; e.currentTarget.style.background = `${GOLD} 0.18)`; e.currentTarget.style.color = TEXT_PRIMARY; }}
        onMouseLeave={(e) => { if (locked) return; e.currentTarget.style.background = 'rgba(20,16,12,0.7)'; e.currentTarget.style.color = TEXT_BODY; }}
      >
        <Unlink style={{ width: '0.7cqw', height: '0.7cqw' }} />
        {locked ? t('Locked') : t('Detach')}
      </button>
    </div>
  );
}

function EquipmentPanel({ equippedCards, sorceryCards, onInspect, onDetach, lockedEquipmentIds }) {
  if (!equippedCards || equippedCards.length === 0) return null;

  return (
    <div
      className="fixed flex flex-col gap-2 p-4"
      style={{
        ...TEXTURED_PANEL_STYLE,
        left: '2cqw',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '14cqw',
        maxHeight: '92cqh',
        boxShadow: `${TEXTURED_PANEL_STYLE.boxShadow}, 0 0 24px rgba(212,168,67,0.18)`,
 zIndex: 1,
 }}
 onClick={(e) => e.stopPropagation()}
 >
 <FourCorners />
 <div className="text-center">
 <h3
 className="text-sm font-bold arena-heading"
 style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}
 >
 {t('Equipment')}
 </h3>
 <div className="text-[0.625cqw] mt-0.5" style={{ color: TEXT_MUTED }}>
 {equippedCards.length === 1 ? t('1 attached') : t('{n} attached', { n: equippedCards.length })}
 </div>
 </div>
 <OrnamentalDivider className="my-1 shrink-0" />
 {/* Fixed scroll viewport. Always reserves space for three
 entries so the panel never resizes with content. Fewer than
 three leaves empty area below; more than three scrolls. The
 inner padding gives the entry hover-lift room to render so
 it isn't clipped at the viewport edge. */}
 <div
 className="flex flex-col overflow-y-auto"
 style={{
 height: EQUIPMENT_SCROLL_HEIGHT,
 gap: EQUIPMENT_ENTRY_GAP,
 paddingTop: EQUIPMENT_SCROLL_PADDING_Y,
 paddingBottom: EQUIPMENT_SCROLL_PADDING_Y,
 paddingLeft: EQUIPMENT_SCROLL_PADDING_X,
 paddingRight: EQUIPMENT_SCROLL_PADDING_X,
 flex: '0 0 auto',
 }}
 >
 {equippedCards.map((eq) => (
 <EquipmentEntry
 key={eq.id}
 eq={eq}
 full={sorceryCards?.find((c) => c.unique_id === eq.cardId)}
 onInspect={onInspect}
 onDetach={onDetach}
 locked={lockedEquipmentIds?.has?.(eq.id) || false}
 lockReason={t("Locked by an aura (can't be detached)")}
 />
 ))}
 </div>
 </div>
 );
}

function InspectorBreadcrumb({ parentCardName, onBack }) {
 if (!parentCardName || !onBack) return null;
 return (
 <button
 type="button"
 onClick={(e) => { e.stopPropagation(); onBack(); }}
 className="fixed inline-flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors"
 style={{
 ...TEXTURED_PANEL_STYLE,
 left: '2cqw',
 top: '2cqw',
 zIndex: 2,
 color: TEXT_PRIMARY,
 fontFamily: "'Geist Variable', sans-serif",
 fontSize: '0.75cqw',
 letterSpacing: '0.08em',
 fontWeight: 600,
 }}
 onMouseEnter={(e) => { e.currentTarget.style.color = ACCENT_GOLD; }}
 onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_PRIMARY; }}
 title={t('Back to {name}', { name: parentCardName })}
    >
      <ChevronLeft style={{ width: '1cqw', height: '1cqw', color: ACCENT_GOLD }} />
      <span style={{ color: TEXT_MUTED, fontWeight: 500 }}>{t('Back to')}</span>
      <span style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>{parentCardName}</span>
    </button>
  );
}

// Rewrite the printed subtype words in a type line using an aura's
// subtype map (Corruptor: Beast → Monster). Whole-word, case-insensitive,
// preserving the replacement's capitalization. No-op without a map.
function applySubtypeMap(typeText, subtypeMap) {
  if (!subtypeMap || !typeText) return typeText;
  let out = typeText;
  for (const [from, to] of Object.entries(subtypeMap)) {
    if (!from || !to || from === to) continue;
    const re = new RegExp(`\\b${from}\\b`, 'gi');
    out = out.replace(re, to.charAt(0).toUpperCase() + to.slice(1));
  }
  return out;
}

export default function CardInspector({
  card: cardProp, imageUrl: imageUrlProp, rarity: rarityProp, foiling: foilingProp, statuses, equippedCards, sorceryCards,
  parentCardName, onBack, onInspectEquipped, onDetachEquipment, lockedEquipmentIds,
  onClose, zoom,
  // Two-sided cards: which face to open on (defaults to front) and an
  // optional callback fired whenever the player flips inside the
  // inspector. Surfaces that own per-tile flip state (deck builder,
  // pack opening) pass both to keep the grid tile in sync with the
  // inspector view. The gameboard passes `initialFace` only so the
  // inspector mirrors the on-table face but flipping inside the
  // inspector doesn't propagate to the table.
  initialFace = 'front',
  onFlipChange,
  // Per-instance subtype rewrite map ({ beast: 'monster', ... }) from a
  // static aura like Corruptor. Lowercase printed subtype → lowercase
  // replacement. When present, the displayed type line shows the
  // transformed identity instead of the printed one.
  subtypeMap = null,
  // Optional secondary catalog card to toggle between. Used by the
  // masked Imposter. `card` is the masked avatar's catalog data
  // (what's on the playmat), and `alternateView` carries the imposter's
  // catalog data so the player can flip the inspector to read the
  // imposter card without touching the game state. Shape:
  //   { card, imageUrl, foiling?, rarity?, label? }
  // `label` is the short noun used on the toggle button. E.g.
  // "Imposter" renders the button as "Show Imposter".
  alternateView = null,
  // Dragonlord companion panel. Shape: { card, imageUrl, active }.
  // `card` is the companion Dragon's catalog object; `active` flips
  // the panel between the merged "Active this turn" presentation and
  // the muted pre-invoke "Set aside" one. Rendered as a third column
  // to the right of the main info panel so the player reads the
  // avatar's own text and the gained text side by side.
  gainedAbilities = null,
  // Whether card automations actually run in the current context. The
  // indicator pill always shows for an automated card (players need to
  // know the card HAS an automation); false renders it muted. The
  // gameboard passes its session flag, everywhere else defaults to on.
  automationsActive = true,
}) {
  if (!cardProp) return null;
  const language = useRulesLanguage();
  // Keyed so switching card, face or language returns to the translation without an effect.

  // Imposter mask alternate-view toggle. Defaults to false so the
  // inspector first paints the masked avatar. The face the table
  // shows. Click flips the displayed card / image / foiling / rarity
  // to the imposter so the player can study the underlying card.
  const [showingAlternate, setShowingAlternate] = useState(false);
  const useAlternate = !!alternateView && showingAlternate;
  const card = useAlternate ? alternateView.card : cardProp;
  const imageUrl = useAlternate ? alternateView.imageUrl : imageUrlProp;
  const foiling = useAlternate ? (alternateView.foiling ?? foilingProp) : foilingProp;
  const rarity = useAlternate ? (alternateView.rarity ?? rarityProp) : rarityProp;
  const alternateButtonLabel = alternateView
    ? (showingAlternate
        ? t('Show {name}', { name: cardProp.name || t('mask') })
        : t('Show {name}', { name: alternateView.label || alternateView.card?.name || t('card') }))
    : null;
  const toggleAlternate = useCallback(() => {
    setShowingAlternate((v) => !v);
  }, []);

  // Play open sound on mount, close sound on unmount
  useEffect(() => {
    playUI(UI.INSPECTOR_OPEN);
    return () => playUI(UI.INSPECTOR_CLOSE);
  }, []);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Two-sided cards (Druid avatar etc.) flip between a front and a
  // back face in place. State seeds from `initialFace` so the
  // inspector opens on whichever face the source surface was showing
  // (deck builder grid tile, on-table gameboard card, etc.) and
  // notifies the parent via `onFlipChange` on every flip. That's
  // what keeps the deck-builder grid tile in sync. The gameboard
  // omits `onFlipChange` so its on-table face stays untouched while
  // the player previews the other side in the inspector.
  const canFlip = Boolean(card.backFace);
  const [face, setFace] = useState(initialFace);
  const handleFlip = useCallback(() => {
    setFace((f) => {
      const next = f === 'front' ? 'back' : 'front';
      onFlipChange?.(next);
      return next;
    });
  }, [onFlipChange]);
  const activeFace = (canFlip && face === 'back') ? card.backFace : null;

  const sourceRulesText = activeFace
    ? (activeFace.functional_text_plain || activeFace.functional_text || '')
    : (card.functional_text_plain || card.functional_text || '');
  const localized = rulesCatalog.card(card, activeFace ? 'back' : 'front', language);
  const rulesText = localized.text;
  const renderRules = (value, depth = 0) => renderTextWithTooltips(value, depth, localized.language);
  // Gained-ability keywords (Airborne on a borrowed Dragon, etc.)
  // join the keyword explainer boxes while the merge is active so
  // the player can read what the borrowed keywords do.
  const gainedText = gainedAbilities?.active
    ? (gainedAbilities.card.functional_text_plain || gainedAbilities.card.functional_text || '')
    : '';
  const keywordAbilities = extractKeywordAbilities(sourceRulesText);
  for (const entry of (gainedText ? extractKeywordAbilities(gainedText) : [])) {
    if (!keywordAbilities.some((k) => k.keyword === entry.keyword)) keywordAbilities.push(entry);
  }

  // Dynamic max-height for the keyword section. CSS percentages don't
  // resolve reliably against the right column's auto-sized containing
  // block, so we measure live and clamp the viewport to the actual
  // pixel gap between the column's bottom and the canvas bottom.
  const rightColRef = useRef(null);
  const [keywordMaxHeight, setKeywordMaxHeight] = useState('28cqh');
  useEffect(() => {
    if (keywordAbilities.length === 0) return;
    const update = () => {
      const canvas = document.querySelector('.app-canvas');
      const col = rightColRef.current;
      if (!canvas || !col) return;
      const canvasRect = canvas.getBoundingClientRect();
      const colRect = col.getBoundingClientRect();
      const availablePx = canvasRect.bottom - colRect.bottom - 24;
      setKeywordMaxHeight(`${Math.max(80, availablePx)}px`);
    };
    const raf = requestAnimationFrame(update);
    const observer = new ResizeObserver(update);
    if (rightColRef.current) observer.observe(rightColRef.current);
    const canvas = document.querySelector('.app-canvas');
    if (canvas) observer.observe(canvas);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [keywordAbilities.length, rulesText]);
  const isSite = card.type === 'Site' || card.played_horizontally;
  const frontImgSrc = imageUrl || card.printings?.[0]?.image_url || '';
  // Back-face printings mirror front printings by slug, so prefer the
  // matching back printing when the player has flipped the card; fall
  // back to the first back printing if no exact match exists.
  const backImgSrc = activeFace
    ? (activeFace.printings?.[0]?.image_url || frontImgSrc)
    : frontImgSrc;
  const imgSrc = activeFace ? backImgSrc : frontImgSrc;
  // Automation indicator (read-only). A card with a sidecar entry shows the
  // pill so players can tell automated cards apart; muted when automations
  // are not running in this context (manual session).
  const hasAutomation = (card.automation || []).length > 0;
  const displayName = activeFace?.name || printingDisplayNameFromImageUrl(card, frontImgSrc);
  const printedTypeText = activeFace?.type_text || card.type_text || card.type || '';
  const displayTypeText = localizeTypeLine(applySubtypeMap(printedTypeText, subtypeMap), language);
  const displayPower = activeFace?.power ?? card.power;
  const displayDefense = activeFace?.defense ?? card.defense;
  const displayHealth = activeFace?.health ?? card.health;
  const isFoil = isFoilFinish(foiling);
  const rarityGlow = RARITY_GLOW[rarity] || RARITY_GLOW.Ordinary;

  // Portal inside .app-canvas so position: fixed becomes canvas-local
  // (the canvas is a transform-containing block via translateZ). cqw
  // units inside also resolve correctly to canvas width.
  return createPortal(
    <motion.div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[1200] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}
      onClick={handleClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.12, ease: 'easeOut' }}
    >
      <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
      <InspectorBreadcrumb parentCardName={parentCardName} onBack={onBack} />
      <EquipmentPanel
        equippedCards={equippedCards}
        sorceryCards={sorceryCards}
        onInspect={onInspectEquipped}
        onDetach={onDetachEquipment}
        lockedEquipmentIds={lockedEquipmentIds}
      />

      <motion.div
        className="relative flex items-start gap-8 max-w-[90cqw] max-h-[90cqh]"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.14, ease: [0.2, 0.7, 0.3, 1] }}
      >
        {/* Left column: card art + the external "View on Curiosa"
            shortcut, centered under the card. Wrapping like this puts
            the link directly below the artwork for portrait cards and
            below the rotated landscape art for sites, since both share
            the same flex-col centering. */}
        <div className="flex flex-col items-center gap-3 flex-shrink-0">
          <InspectorCard
            imgSrc={imgSrc}
            cardName={displayName}
            isSite={isSite}
            isFoil={isFoil}
            foiling={foiling}
            hasZones={foilZonesForCard(card)}
          />

          {/* Action row under the card art. The Curiosa link stays
              centered; the flip button (only for two-sided cards)
              anchors to the right edge so it lines up with the
              card's right edge. The same position as in the deck
              builder / pack opening grids. `alignSelf: stretch`
              overrides the column's `items-center` so this row
              spans the column's full natural width (= card width). */}
          {(curiosaCardUrl(card.name) || canFlip || alternateView) ? (
            <div className="relative flex items-center justify-center" style={{ alignSelf: 'stretch' }}>
              {curiosaCardUrl(card.name) ? (
                <a
                  href={curiosaCardUrl(card.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors hover:brightness-125"
                  style={{
                    background: `${GOLD} 0.08)`,
                    border: `1px solid ${GOLD} 0.3)`,
                    color: ACCENT_GOLD,
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  }}
                >
                  <ExternalLink style={{ width: '0.8125cqw', height: '0.8125cqw' }} />
                  {rulesLabel('View on Curiosa', language)}
                </a>
              ) : null}
              {canFlip ? (
                <div className="absolute" style={{ right: 0, top: '50%', transform: 'translateY(-50%)' }}>
                  <CardFlipButton
                    position="inline"
                    face={face}
                    onFlip={handleFlip}
                    size="1.75cqw"
                  />
                </div>
              ) : null}
              {/* Imposter alternate view. Swaps the inspector between
                  the masked avatar and the imposter card. No effect on
                  the on-table card; preview only. Slotted to the right
                  edge like CardFlipButton; both never appear together
                  in practice (no avatar is both two-sided AND wearing
                  a mask), but if they did, this button stacks to the
                  left of the flip control. */}
              {alternateView ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleAlternate(); }}
                  aria-label={alternateButtonLabel}
                  title={alternateButtonLabel}
                  className="absolute inline-flex items-center justify-center transition-all duration-150 hover:scale-110 active:scale-95 cursor-pointer"
                  style={{
                    right: canFlip ? '2.5cqw' : 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '1.75cqw',
                    height: '1.75cqw',
                    borderRadius: '50%',
                    background: showingAlternate
                      ? 'rgba(20, 14, 8, 0.78)'
                      : `radial-gradient(circle at 35% 30%, ${ACCENT_GOLD}, #7a5414 85%)`,
                    border: `1px solid ${showingAlternate ? `${GOLD} 0.55)` : ACCENT_GOLD}`,
                    color: showingAlternate ? '#fbe9b8' : '#1a1208',
                    boxShadow: showingAlternate
                      ? '0 0 0 1px rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.55)'
                      : `0 0 0 1px rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.55), 0 0 14px ${GOLD} 0.6)`,
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    padding: 0,
                  }}
                >
                  <IconMask size={20} crossed={showingAlternate} />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Right column. Outer wrapper has no overflow so the main
            panel's rarity-keyed rim glow can extend past the panel
            without clipping. The keyword section below the panel is
            absolutely positioned so it stays out of the column's
            natural height. That keeps the card + main-info pair
            fixed at the modal's centered position regardless of how
            many keyword boxes appear underneath. */}
        <div
          ref={rightColRef}
          className="relative flex flex-col gap-3 min-w-[21.25cqw] max-w-[30cqw]"
        >
          {/* Active statuses applied to this specific card instance.
              Sits above the main info card so the player sees what's
              going on with this particular card before reading the
              rules text. Empty when the card has no statuses, in
              which case the row collapses entirely. */}
          <ActiveStatusRow statuses={statuses} language={language} />

          {/* Main card info. Same textured carved-stone recipe used by
              the FirstPlayerDiceModal (CONTENT_BG_DEFAULT overlay + gold
              rim + layered shadows), with the rarity-keyed rim glow
              stacked on top so higher-rarity cards bloom brighter
              (avatar = ember red, unique = gold, etc.). */}
          <div
            className="relative p-5"
            style={{
              ...TEXTURED_PANEL_STYLE,
              // Foil adds a directional inset sheen so the white edge reads
              // like worn foil catching light from the top-left, not a flat
              // uniform line.
              boxShadow: isFoil
                ? `${TEXTURED_PANEL_STYLE.boxShadow}, ${rarityGlow}, inset 1px 1px 0 rgba(255,255,255,0.3), inset -1px -1px 0 rgba(255,255,255,0.04)`
                : `${TEXTURED_PANEL_STYLE.boxShadow}, ${rarityGlow}`,
              // Foil cards use a white outer stroke; the strong top-left
              // sheen vs the faint bottom-right gives the lit/worn contrast.
              ...(isFoil ? { border: '1px solid rgba(255,255,255,0.4)' } : {}),
            }}
          >
            {isFoil ? (
              // Per-corner alpha variation gives the plating an uneven,
              // used look instead of four identical white brackets.
              <>
                <CornerPlating position="top-left" color="rgba(255,255,255,0.5)" />
                <CornerPlating position="top-right" color="rgba(255,255,255,0.4)" />
                <CornerPlating position="bottom-left" color="rgba(255,255,255,0.44)" />
                <CornerPlating position="bottom-right" color="rgba(255,255,255,0.3)" />
              </>
            ) : (
              <FourCorners />
            )}

            {isFoil ? (
              <div
                className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))',
                  border: '1px solid rgba(255,255,255,0.22)',
                }}
              >
                <Sparkles style={{ width: "0.8125cqw", height: "0.8125cqw", color: 'rgba(255,255,255,0.9)' }} />
                <span className="text-[0.625cqw] font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  {t(FOIL_LABEL[foiling])}
                </span>
              </div>
            ) : null}

            <h2 className="text-lg font-bold arena-heading pr-24" style={{ color: isFoil ? 'rgba(255,255,255,0.9)' : TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{displayName}</h2>
            <div className="mt-1.5 flex items-center gap-2">
              {rarity ? <span className="text-xs font-semibold" style={{ color: RARITY_COLORS[rarity] || TEXT_MUTED }}>{rulesCatalog.term(rarity, language)}</span> : null}
              <span className="text-xs" style={{ color: TEXT_MUTED }}>{displayTypeText}</span>
            </div>

            {language !== 'en' && !localized.translated && rulesText ? (
              <p className="mt-2 text-xs" style={{ color: TEXT_MUTED }}>{rulesLabel('Translation unavailable; showing English.', language)}</p>
            ) : null}

            {rulesText ? (
              <>
                <div className="my-3 h-px" style={{ background: isFoil ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.07) 16%, rgba(255,255,255,0.26) 44%, rgba(255,255,255,0.12) 66%, rgba(255,255,255,0.03) 88%, transparent 100%)' : `linear-gradient(90deg, transparent, ${GOLD} 0.2), transparent)` }} />
                <div
                  lang={localized.language}
                  data-card-rules=""
                  className="text-sm leading-relaxed overflow-y-auto pr-1"
                  style={{
                    color: TEXT_BODY,
                    maxHeight: '25cqh',
                    // Reserve room for the absolutely-positioned pill at
                    // bottom-3 right-3 when no stats row sits between the
                    // rules text and the panel bottom.
                    paddingBottom: (hasAutomation && !(displayPower || displayDefense)) ? '2cqw' : undefined,
                  }}
                >
                  {renderRules(rulesText)}
                </div>
              </>
            ) : null}

            {(displayPower || displayDefense) ? (
              <div className="mt-3 flex gap-4 text-sm font-semibold">
                {displayPower ? <span style={{ color: STAT_COLORS.ATK }}>{rulesLabel('ATK', language)} {displayPower}</span> : null}
                {displayDefense ? <span style={{ color: STAT_COLORS.DEF }}>{rulesLabel('DEF', language)} {displayDefense}</span> : null}
                {displayHealth ? <span style={{ color: STAT_COLORS.HP }}>{rulesLabel('HP', language)} {displayHealth}</span> : null}
                {card.cost ? <span style={{ color: STAT_COLORS.Cost }}>{rulesLabel('Cost', language)} {card.cost}</span> : null}
              </div>
            ) : null}

            {hasAutomation ? (
              <Tooltip
                content={automationsActive ? rulesLabel('Automated', language) : rulesLabel('Automation available. Automations are off in this match', language)}
                side="top"
              >
                <span
                  className="absolute bottom-3 right-3 flex items-center"
                  style={{ color: automationsActive ? 'rgb(232, 195, 110)' : TEXT_MUTED }}
                >
                  <Sparkles style={{ width: '1.1cqw', height: '1.1cqw' }} />
                </span>
              </Tooltip>
            ) : null}
          </div>

          {/* Keyword ability boxes. Absolutely positioned just below
              the right column so they don't contribute to the modal's
              natural height. Card + main info stay fixed at the
              modal's centered position regardless of how many keyword
              boxes appear. left/right: 0 makes this section share the
              column's exact horizontal bounds so each KeywordBox
              lines up flush with the main info panel above. maxHeight
              is measured live (see effect above) so the viewport
              always fills the gap between the column's bottom and
              the canvas bottom. As tall as possible, never off-screen. */}
          {keywordAbilities.length > 0 ? (
            <div
              className="absolute left-0 right-0 flex flex-col gap-2 overflow-y-auto"
              style={{
                top: 'calc(100% + 0.75cqw)',
                maxHeight: keywordMaxHeight,
              }}
            >
                  {keywordAbilities.map(({ keyword, description }) => {
                    const help = rulesCatalog.entry(keyword, localized.language);
                    return <KeywordBox key={`${keyword}-${language}`}
                      keyword={help?.label || keyword} description={help?.description || description}
                      language={help?.language || 'en'} renderText={(value, depth = 0) => renderTextWithTooltips(value, depth, help?.language || 'en')} />;
                  })}
            </div>
          ) : null}
        </div>

        {/* Dragonlord companion. Anchored to the RIGHT of the
            centered card + info group via absolute positioning so it
            never contributes to the flex row's width. Participating
            in the layout would shift the centered pair left and
            collide with the fixed equipment panel on the canvas's
            left edge. */}
        {gainedAbilities ? (
          <div
            className="absolute"
            style={{ left: 'calc(100% + 2cqw)', top: 0 }}
          >
            <GainedAbilitiesPanel
              language={language}
              gained={gainedAbilities}
            />
          </div>
        ) : null}
      </motion.div>
    </motion.div>,
    document.querySelector('.app-canvas') ?? document.body,
  );
}
